import type { AgentModel, BrowserSessionModel, ScriptResourceModel } from "@src/generated/prisma/models";
import { useRestAPI } from "../hooks/useRestAPI";
import { useAgentStore } from "../stores/agentStore";
import { useSessionStore } from "../stores/sessionStore";
import React, { useCallback, useImperativeHandle, useMemo, useState } from "react";
import { Button, DatePicker, Drawer, Flex, Form, Select } from "antd";
import { REQUIRED_RULES } from "../utils";
import { PlayArrowOutlined, SyncOutlined } from "@mui/icons-material";
import type { Res, UserScriptModelFix } from "../utils/types";
import type { ScriptActionPayload } from "@src/utils/agent";
import { useAuthHeaders } from "../hooks/useAuthHeaders";
import { useMessageHandler } from "../hooks/useMessageHandler";
import zhCN from 'antd/es/date-picker/locale/zh_CN';
import dayjs from 'dayjs';

interface FormData {
  agent_id?: string;
  session_ids?: string[];
  script_id?: string;
  resource_ids?: string[];
  runAt?: number;
}

interface Props {}

export interface ActionExecutorHandler {
  open: (data: FormData) => void;
}

export const ActionExecutor = React.forwardRef((props: Props, ref: React.Ref<ActionExecutorHandler>) => {
  const { listReq: agents } = useRestAPI<AgentModel>('/api/agents');
  const { listReq: sessions } = useRestAPI<BrowserSessionModel>('/api/sessions');
  const { listReq: scripts } = useRestAPI<UserScriptModelFix>('/api/scripts');
  const { listReq: scriptsPub } = useRestAPI<UserScriptModelFix>('/api/scripts?publish=true');
  const { listReq: resources } = useRestAPI<ScriptResourceModel>('/api/resources');
  const [form] = Form.useForm<FormData>();
  const watcher = Form.useWatch([], form);
  const onlineAgents = useAgentStore(store => store.online)
  const onlineSesions = useSessionStore(store => store.sessions);
  const agent = watcher?.agent_id;
  const agentOptions = useMemo(() => agents.data?.data?.map(e => ({ label: e.name, value: e.id, disabled: !onlineAgents[e.id] })) || [], [agents, onlineAgents]);
  const sessionOptions = useMemo(() => {
    const list = sessions.data?.data || [];
    if (!agent) return [];
    return list.filter(s => s.agent_id === agent).map(e => ({
      label: e.name,
      value: e.id,
      disabled: !onlineSesions.includes(e.id),
    }));
  }, [agent, sessions, onlineSesions]);
  const scriptOptions = useMemo<{ label: string; value: string; }[]>(() => {
    const pub = scriptsPub.data?.data?.map(e => [e.id, { label: `公共：${e.name}`, value: e.id }]) || [];
    const pri = scripts.data?.data?.map(e => [e.id, { label: `私有：${e.name}`, value: e.id }]) || [];
    return Object.values(Object.fromEntries([...pub, ...pri]));
  }, [scripts, scriptsPub]);
  const resourceOptions = resources.data?.data?.map(e => ({ label: e.name, value: e.id })) || [];
  const [open, setOpen] = useState(false);
  useImperativeHandle(ref, () => ({
    open: (formdata) => {
      form.resetFields();
      form.setFieldsValue(formdata)
      setOpen(true);
    },
  }), [form]);
  const { getAuthHeader } = useAuthHeaders();
  const { handleMessage } = useMessageHandler();
  const handleExecute = useCallback(async () => {
    const { runAt, ...data} = await form.validateFields();
    const headers = await getAuthHeader();
    if (!headers) return;
    console.debug('Execute', data);
    fetch('/api/run-script', {
      headers,
      method: 'PUT',
      body: JSON.stringify({ ...data, runAt: runAt ? dayjs(runAt).toDate().getTime() : undefined, }),
    })
      .then(res => res.json())
      .then((data: Res<any>) => handleMessage(data));
  }, [form]);
  const resourceRequired = useMemo(() => {
    if (!watcher?.script_id) return false;
    const actions = scripts.data?.data?.find(e => e.id === watcher.script_id)?.actions || [];
    const pubActions = scriptsPub.data?.data?.find(e => e.id === watcher.script_id)?.actions || [];
    return [...actions, ...pubActions]
      .some(e => (e.payload as ScriptActionPayload<'INPUT'>).type === 'res_key')
  }, [watcher?.script_id, scripts, scriptsPub]);
  const handleRefresh = useCallback(async () => {
    await Promise.all([
      agents.refreshAsync(),
      sessions.refreshAsync(),
      scripts.refreshAsync(),
      resources.refreshAsync(),
      scriptsPub.refreshAsync(),
    ]);
  }, [agents, sessions, scripts, resources]);
  return (
    <Drawer
      forceRender
      size={600}
      title='执行脚本'
      open={open} onClose={() => setOpen(false)} mask={{ closable: false }}
    >
      <Flex vertical gap={24}>
        <Button icon={<SyncOutlined />} onClick={handleRefresh}>刷新</Button>
        <Form form={form} layout="vertical">
          <Form.Item label='代理节点' name={'agent_id'}>
            <Select allowClear placeholder='所有代理节点所有浏览器都执行' options={agentOptions} onChange={v => {
              form.setFieldsValue({ session_ids: [] });
            }} />
          </Form.Item>
          <Form.Item hidden={!watcher?.agent_id} label='浏览器' name={'session_ids'}>
            <Select allowClear mode="multiple" placeholder='所有浏览器执行' options={sessionOptions} />
          </Form.Item>
          <Form.Item rules={[REQUIRED_RULES]} label='动作' name={'script_id'}><Select allowClear options={scriptOptions} /></Form.Item>
          <Form.Item rules={[{ required: resourceRequired }]} label='资源' name={'resource_ids'}><Select mode="multiple" options={resourceOptions} /></Form.Item>
          <Form.Item label='执行时间' name={'runAt'}>
            <DatePicker
              showTime
              locale={zhCN}
              placement="bottomRight"
              placeholder="执行时间" style={{ width: '100%' }}
            />
          </Form.Item>
        </Form>
        <Button block icon={<PlayArrowOutlined />} onClick={handleExecute}>执行</Button>
      </Flex>
    </Drawer>
  );
});